<template>
  <div class="app-sidebar">
    <div class="app-sidebar__nav">
      <v-expansion-panels v-model="expandedPanels" multiple flat class="app-sidebar__panels">
        <v-expansion-panel v-for="(route, index) in navigationRoutes.routes" :key="index" :value="index" elevation="0">
          <v-expansion-panel-title
            hide-actions
            class="app-sidebar__panel-title"
            :class="{ 'app-sidebar__item--disabled': isRouteDisabled(route) }"
            @click="handleRouteClick(route, $event)"
          >
            <v-list-item
              :to="getRouteTo(route)"
              :active="routeHasActiveChild(route)"
              :disabled="isRouteDisabled(route)"
              class="app-sidebar__item w-full"
              rounded="0"
            >
              <template #prepend>
                <v-icon v-if="route.meta?.icon" :icon="route.meta.icon" size="20" class="app-sidebar__icon" />
              </template>
              <v-list-item-title class="app-sidebar__label">
                {{ t(route.displayName) }}
              </v-list-item-title>
              <template v-if="route.children" #append>
                <v-icon :icon="expandedPanels.includes(index) ? 'expand_less' : 'expand_more'" size="20" />
              </template>
            </v-list-item>
          </v-expansion-panel-title>

          <v-expansion-panel-text v-if="route.children" class="app-sidebar__children">
            <v-list density="compact" nav class="app-sidebar__child-list">
              <v-list-item
                v-for="(childRoute, index2) in route.children"
                :key="index2"
                :to="isRouteDisabled(childRoute) ? undefined : { name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :disabled="isRouteDisabled(childRoute)"
                class="app-sidebar__item app-sidebar__item--child w-full"
                :class="{ 'app-sidebar__item--active': isActiveChildRoute(childRoute) }"
                rounded="0"
                @click="handleRouteClick(childRoute, $event)"
              >
                <v-list-item-title class="app-sidebar__label">
                  {{ t(childRoute.displayName) }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <SetupProgressWidget />
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth-store'
import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import SetupProgressWidget from './SetupProgressWidget.vue'
import { isShippingCostNavigationRoute } from '../../utils/shippingCostNavigation'

export default defineComponent({
  name: 'Sidebar',
  setup: () => {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const authStore = useAuthStore()

    const expandedPanels = ref<number[]>([])

    const hasStores = computed(() => {
      if (authStore.tokenType !== 'USER') return true
      return (authStore.user?.stores ?? 0) > 0
    })

    const isRouteDisabled = (routeItem: INavigationRoute) => {
      if (hasStores.value) return false
      if (routeItem.name === 'store-management') return false
      return true
    }

    const getRouteTo = (routeItem: INavigationRoute) => {
      if (isRouteDisabled(routeItem)) return undefined
      if (routeItem.name === 'product-report' && routeItem.children && routeItem.children.length > 0) {
        return { name: routeItem.children[0].name }
      }
      if (routeItem.children && routeItem.name !== 'product-report') return undefined
      if (routeItem.meta?.externalUrl) return undefined
      return { name: routeItem.name }
    }

    const handleRouteClick = (routeItem: INavigationRoute, event: Event) => {
      if (isRouteDisabled(routeItem)) {
        event.preventDefault()
        event.stopPropagation()
        return false
      }

      if (routeItem.name === 'product-report' && routeItem.children && routeItem.children.length > 0) {
        event.preventDefault()
        event.stopPropagation()
        router.push({ name: routeItem.children[0].name })
        return false
      }

      if (routeItem.meta?.externalUrl) {
        event.preventDefault()
        event.stopPropagation()
        window.open(routeItem.meta.externalUrl, '_blank', 'noopener,noreferrer')
        return false
      }
    }

    const matchesShippingCostNav = () => isShippingCostNavigationRoute(route.name, route.path, route.matched)

    const isActiveChildRoute = (child: INavigationRoute) => {
      if (route.name === child.name) return true
      if (child.name === 'shipping-cost' && matchesShippingCostNav()) return true
      if (child.name === 'campaign-attribution' && route.name === 'auto-match-suggestions') return true
      if (child.name === 'marketing-channel' && route.name === 'marketing-channel-manage') return true
      if (child.name === 'dropship-pod' && route.name === 'dropship-pod-connect') return true
      return false
    }

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name || route.path.endsWith(`${section.name}`)
      }

      return section.children.some(
        ({ name }) =>
          route.name === name ||
          route.path.endsWith(`/${name}`) ||
          (name === 'shipping-cost' && matchesShippingCostNav()) ||
          (name === 'campaign-attribution' && route.name === 'auto-match-suggestions') ||
          (name === 'marketing-channel' && route.name === 'marketing-channel-manage') ||
          (name === 'dropship-pod' && route.name === 'dropship-pod-connect'),
      )
    }

    const setActiveExpand = () => {
      expandedPanels.value = navigationRoutes.routes
        .map((navRoute: INavigationRoute, index: number) => (routeHasActiveChild(navRoute) ? index : -1))
        .filter((index) => index >= 0)
    }

    watch(() => [route.fullPath, route.name] as const, setActiveExpand, { immediate: true })

    return {
      expandedPanels,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      isRouteDisabled,
      handleRouteClick,
      getRouteTo,
    }
  },
  components: {
    SetupProgressWidget,
  },
})
</script>

<style lang="scss" scoped>
.app-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding-top: 0.5rem;
}

.app-sidebar__nav {
  flex: 1;
  overflow-y: auto;
  width: 100%;
}

.app-sidebar__panels {
  background: transparent;
  width: 100%;
}

.app-sidebar__panel-title {
  min-height: 44px;
  padding: 0;
  width: 100%;
  display: block;
}

.app-sidebar__panel-title :deep(.v-expansion-panel-title__overlay) {
  opacity: 0;
}

.app-sidebar__children :deep(.v-expansion-panel-text__wrapper) {
  padding: 0;
}

.app-sidebar__child-list {
  padding: 0;
  width: 100%;
}

.app-sidebar__item {
  min-height: 44px;
  padding-inline: 1rem;
  width: 100%;
  color: rgb(var(--v-theme-text-primary));
}

.app-sidebar__item :deep(.v-list-item-title),
.app-sidebar__item :deep(.v-icon) {
  color: inherit;
}

.app-sidebar__item :deep(.v-list-item__content) {
  flex: 1 1 auto;
  min-width: 0;
}

.app-sidebar__item :deep(.v-list-item__append) {
  margin-inline-start: auto;
}

.app-sidebar__item--child {
  padding-left: 2.75rem !important;
}

.app-sidebar__label {
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.25;
}

.app-sidebar__icon {
  opacity: 0.9;
  margin-inline-end: 0.75rem;
}

.app-sidebar__item--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.app-sidebar__item.v-list-item--active,
.app-sidebar__item.app-sidebar__item--active {
  background: color-mix(in srgb, rgb(var(--v-theme-primary)) 18%, rgb(var(--v-theme-background-sidebar)));
  color: rgb(var(--v-theme-primary));
}

.app-sidebar__item.v-list-item--active .app-sidebar__label,
.app-sidebar__item.app-sidebar__item--active .app-sidebar__label {
  font-weight: 700;
}

.app-sidebar__item:hover:not(.v-list-item--disabled):not(.v-list-item--active):not(.app-sidebar__item--active) {
  background: color-mix(in srgb, rgb(var(--v-theme-primary)) 10%, rgb(var(--v-theme-background-sidebar)));
}

.app-sidebar__panels :deep(.v-expansion-panel) {
  background: transparent;
  width: 100%;
}

.app-sidebar__panels :deep(.v-expansion-panel-title) {
  width: 100%;
  padding: 0;
  display: block;
}

.app-sidebar__panels :deep(.v-expansion-panel::before) {
  box-shadow: none;
}

.app-sidebar__panels :deep(.v-expansion-panel::after) {
  border: none;
}

:global(.v-theme--dark) {
  .app-sidebar__item.v-list-item--active,
  .app-sidebar__item.app-sidebar__item--active {
    color: rgb(var(--v-theme-focus));
  }
}
</style>
